import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { investorPortalApiClient } from '../utils/api-client';
import { decodeTokenPayload } from '../../auth/services/auth-service';
import { portalQueryKeys } from './queryKeys';

interface SessionInfo {
  session_id: string;
  admin_user_id: string | null;
  admin_user_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  investor_id: string | null;
  investor_name: string | null;
}

interface ImpersonationState {
  isImpersonation: boolean;
  adminUserName: string | null;
  contactName: string | null;
  investorName: string | null;
  sessionId: string | null;
  isLoading: boolean;
  endImpersonation: () => void;
  refreshSessionInfo: () => Promise<void>;
}

function isImpersonationToken(): boolean {
  const token = localStorage.getItem('investor_portal_token');
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  return payload?.is_impersonation === true;
}

async function fetchSessionInfo(): Promise<SessionInfo | null> {
  return investorPortalApiClient.get<SessionInfo>('/portal/auth/session-info');
}

export function useImpersonation(): ImpersonationState {
  const queryClient = useQueryClient();
  const isImpersonation = isImpersonationToken();

  const { data: sessionInfo, isLoading } = useQuery({
    queryKey: portalQueryKeys.session.info(),
    queryFn: fetchSessionInfo,
    staleTime: 5 * 60 * 1000,
    enabled: isImpersonation,
  });

  const endImpersonation = useCallback(() => {
    localStorage.removeItem('investor_portal_token');
    localStorage.removeItem('investor_portal_refresh_token');
    localStorage.removeItem('investor_portal_token_expires_at');
    localStorage.removeItem('investor_portal_session_id');
    localStorage.removeItem('investor_portal_contact');

    queryClient.invalidateQueries({ queryKey: portalQueryKeys.session.all() });

    window.close();

    setTimeout(() => {
      window.location.href = '/portal/login';
    }, 100);
  }, [queryClient]);

  const refreshSessionInfo = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: portalQueryKeys.session.info(),
    });
  }, [queryClient]);

  return {
    isImpersonation,
    adminUserName: sessionInfo?.admin_user_name ?? null,
    contactName: sessionInfo?.contact_name ?? null,
    investorName: sessionInfo?.investor_name ?? null,
    sessionId: sessionInfo?.session_id ?? null,
    isLoading,
    endImpersonation,
    refreshSessionInfo,
  };
}
